/**
 * Deliverable Modal — center overlay that expands to show task results.
 * Triggered from task queue "View Findings" button or inline chat card.
 * Programmatic DOM, glass-morphism + dark aesthetic.
 */

import type { StockAnalysisData } from '@/types';

export class DeliverableModal {
  private overlay: HTMLElement;
  private contentEl: HTMLElement;
  private isOpen = false;

  constructor() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'deliverable-modal-overlay';
    this.overlay.innerHTML = `
      <div class="deliverable-modal">
        <div class="deliverable-modal-header">
          <span class="deliverable-modal-title"></span>
          <button class="deliverable-modal-close">×</button>
        </div>
        <div class="deliverable-modal-content"></div>
      </div>
    `;
    document.body.appendChild(this.overlay);

    this.contentEl = this.overlay.querySelector('.deliverable-modal-content')!;

    // Close on overlay click (not modal click)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    // Close button
    this.overlay.querySelector('.deliverable-modal-close')!.addEventListener('click', () => {
      this.close();
    });

    // ESC key
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  showStockAnalysis(data: StockAnalysisData): void {
    const titleEl = this.overlay.querySelector('.deliverable-modal-title')!;
    titleEl.textContent = `${data.ticker} — Stock Analysis`;

    const isPositive = data.change.startsWith('+');
    const changeColor = isPositive ? '#22c55e' : '#ef4444';
    const arrow = isPositive ? '↑' : '↓';

    this.contentEl.innerHTML = `
      <div class="dm-stock-hero">
        <div class="dm-stock-left">
          <div class="dm-stock-symbol">${data.ticker}</div>
          <div class="dm-stock-name">${data.companyName}</div>
        </div>
        <div class="dm-stock-right">
          <div class="dm-stock-price">${data.currentPrice}</div>
          <div class="dm-stock-change" style="color: ${changeColor}">${arrow} ${data.change} (${data.changePercent})</div>
        </div>
      </div>
      <canvas class="dm-stock-chart" width="720" height="280"></canvas>
      <div class="dm-stock-details">
        <div class="dm-stock-detail-row">
          <span class="dm-stock-detail-label">Market Cap</span>
          <span class="dm-stock-detail-value">${data.marketCap}</span>
        </div>
        <div class="dm-stock-detail-row">
          <span class="dm-stock-detail-label">Period</span>
          <span class="dm-stock-detail-value">Last 5 Trading Days</span>
        </div>
      </div>
      <div class="dm-stock-summary">${data.summary}</div>
      <div class="dm-stock-badge">
        <span class="dm-stock-badge-icon">🤖</span>
        <span class="dm-stock-badge-text">Researched by Sam • Live data via web search</span>
      </div>
    `;

    // Draw the larger chart
    const canvas = this.contentEl.querySelector('.dm-stock-chart') as HTMLCanvasElement;
    if (canvas && data.pricePoints.length > 1) {
      this.drawChart(canvas, data.pricePoints, isPositive);
    }

    this.open();
  }

  private drawChart(
    canvas: HTMLCanvasElement,
    points: Array<{ label: string; price: number }>,
    isPositive: boolean,
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const pad = { top: 25, right: 30, bottom: 40, left: 65 };
    const chartW = W - pad.left - pad.right;
    const chartH = H - pad.top - pad.bottom;

    const prices = points.map(p => p.price);
    const minP = Math.min(...prices) * 0.997;
    const maxP = Math.max(...prices) * 1.003;
    const range = maxP - minP || 1;

    // Background — matches modal bg
    ctx.fillStyle = 'rgba(10, 15, 30, 1)';
    ctx.fillRect(0, 0, W, H);

    // Grid lines
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = pad.top + (chartH / 5) * i;
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(W - pad.right, y);
      ctx.stroke();

      const price = maxP - (range / 5) * i;
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.font = '13px system-ui';
      ctx.textAlign = 'right';
      ctx.fillText(`$${price.toFixed(2)}`, pad.left - 10, y + 5);
    }

    // Chart points
    const chartPoints: [number, number][] = points.map((p, i) => [
      pad.left + (i / (points.length - 1)) * chartW,
      pad.top + (1 - (p.price - minP) / range) * chartH,
    ]);

    // Gradient fill
    const gradient = ctx.createLinearGradient(0, pad.top, 0, pad.top + chartH);
    const lineColor = isPositive ? '#22c55e' : '#ef4444';
    gradient.addColorStop(0, isPositive ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)');
    gradient.addColorStop(1, 'rgba(0,0,0,0)');

    // Fill area
    ctx.beginPath();
    ctx.moveTo(chartPoints[0][0], chartPoints[0][1]);
    for (let i = 1; i < chartPoints.length; i++) {
      const px = chartPoints[i - 1][0];
      const py = chartPoints[i - 1][1];
      const cx = chartPoints[i][0];
      const cy = chartPoints[i][1];
      ctx.quadraticCurveTo(px, py, (px + cx) / 2, (py + cy) / 2);
    }
    const last = chartPoints[chartPoints.length - 1];
    ctx.lineTo(last[0], last[1]);
    ctx.lineTo(last[0], pad.top + chartH);
    ctx.lineTo(chartPoints[0][0], pad.top + chartH);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    // Line
    ctx.beginPath();
    ctx.moveTo(chartPoints[0][0], chartPoints[0][1]);
    for (let i = 1; i < chartPoints.length; i++) {
      const px = chartPoints[i - 1][0];
      const py = chartPoints[i - 1][1];
      const cx = chartPoints[i][0];
      const cy = chartPoints[i][1];
      ctx.quadraticCurveTo(px, py, (px + cx) / 2, (py + cy) / 2);
    }
    ctx.lineTo(last[0], last[1]);
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 3;
    ctx.stroke();

    // Data dots
    for (let i = 0; i < chartPoints.length; i++) {
      const [x, y] = chartPoints[i];
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = lineColor;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();

      // Price label above dot
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.font = '11px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText(`$${points[i].price.toFixed(2)}`, x, y - 12);
    }

    // X-axis labels
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = '13px system-ui';
    ctx.textAlign = 'center';
    for (let i = 0; i < points.length; i++) {
      ctx.fillText(points[i].label, chartPoints[i][0], H - 10);
    }
  }

  private open(): void {
    this.isOpen = true;
    this.overlay.classList.add('open');
  }

  close(): void {
    this.isOpen = false;
    this.overlay.classList.remove('open');
  }

  dispose(): void {
    this.overlay.remove();
  }
}
